import React, {useContext, useEffect, useRef, useState} from 'react'
import {useNavigate, useParams, useBlocker} from 'react-router-dom'
import AppContext, {AppContextData} from '../../appContext'
import LimeController from '../../controllers/LimeController'
import LimeProgramResponse from '../../controllers/LimeProgramResponse'
import LimeProgramLabInputs from '../../controllers/LimeProgramLabInputs'
import LimeProgramProductSources from '../../controllers/LimeProgramProductSources'
import LimePlayFieldRow from '../../controllers/LimePlayFieldRow'
import LimeTemplateBasicResult from '../../controllers/LimeTemplateBasicResult'
import LimeProductResponse from '../../controllers/LimeProductResponse'
import LimeProductType from '../../controllers/LimeProductType'
import LabInputUnit from '../../controllers/LabInputUnit'
import {dateFormat} from '../../date'
import Dialog from '../../components/Dialog'
import ProgramApprove from '../program/ProgramApprove'
import UserController from '../../controllers/UserController'
import {showSuccessOrFailed} from '../../Snacks'
import Failed from '../../components/Failed'
import {calculateLandRows, buildProductValues, CalcOptions, extractCalculatedOutputs} from './limeCalcs'
import LimePlayfieldUnified, {ProductNames} from './LimePlayfieldUnified'
import ApplicationDataDialog from './ApplicationDataDialog'
import {useBoundary} from '../../controllers/useBoundary'
import SelectNumber from '../../components/SelectNumber'
import SelectNumberNullable from '../../components/SelectNumberNullable'
import Input from '../../components/Input'
import Number from '../../components/Number'
import {ChevronLeftIcon, ChevronRightIcon} from '@heroicons/react/24/outline'
import {switcher} from '../../wrapper'

type ActiveTab = 'master' | 'playfield'

interface LimeProgramProps {
    id?: number
    footer?: React.ReactNode
}

function getProductNames(products: LimeProductResponse[], productSources: LimeProgramProductSources | null, context: AppContextData): ProductNames {
    const findProduct = (id: number | null | undefined, fallbackKey: string) => {
        if (!id) return context.word(fallbackKey);
        return products.find(p => p.id === id)?.name ?? context.word(fallbackKey);    
    }
    
    return {
        calciticLime: findProduct(productSources?.calciticLimeProductId, 'calcitic_lime'),
        dolomiticLime: findProduct(productSources?.dolomiticLimeProductId, 'dolomitic_lime'),
        gypsum: findProduct(productSources?.gypsumProductId, 'gypsum'),
        potassium: findProduct(productSources?.potassiumProductId, 'potassium'),
        phosphate: findProduct(productSources?.phosphateProductId, 'phosphate')
    };
}

const LimeProgram: React.FC<LimeProgramProps> = (props) => {
    const context = useContext(AppContext)
    const navigate = useNavigate()
    const params = useParams()
    const id = props.id ?? parseInt(params.id as string)

    const [program, setProgram] = useState<LimeProgramResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [showApprover, setShowApprover] = useState(false)
    const [warningPopup, setWarningPopup] = useState(false)

    // Editable state
    const [name, setName] = useState('')
    const [regionId, setRegionId] = useState<number | null>(null)
    const [regionName, setRegionName] = useState('')
    const [labInputUnit, setLabInputUnit] = useState<LabInputUnit>(LabInputUnit.MgKgPpm)
    const [productSources, setProductSources] = useState<LimeProgramProductSources | null>(null)
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
    const [templates, setTemplates] = useState<LimeTemplateBasicResult[]>([])
    const [products, setProducts] = useState<LimeProductResponse[]>([])
    const [labInputs, setLabInputs] = useState<LimeProgramLabInputs>({rows: []})
    const [calculatedRows, setCalculatedRows] = useState<LimePlayFieldRow[]>([])
    const [labInputsChanged, setLabInputsChanged] = useState(false)
    const [tab, setTab] = useState<ActiveTab>('master')
    const [showApplicationData, setShowApplicationData] = useState(false)

    // Fetch boundary for application data map
    const {coordinates: boundaryCoordinates, loading: boundaryLoading, error: boundaryError} = useBoundary(program?.landId)

    // Track original values
    const originalRef = useRef<{
        name: string
        productSources: LimeProgramProductSources
        templateId: number
        labInputs: LimeProgramLabInputs
    } | null>(null)

    useEffect(() => {
        if (isNaN(id)) {
            navigate('/liming/programs')
            return
        }

        // First load the program to get its region, then load products by region
        LimeController.getProgram(id).then(programData => {
            setProgram(programData)
            setName(programData.name)
            setRegionId(programData.regionId)
            setRegionName(programData.regionName)
            setLabInputUnit(programData.labInputUnit)
            setProductSources(programData.productSources)
            setSelectedTemplateId(programData.limeTemplateId)
            setLabInputs(programData.labInputs)

            // Now load templates and products filtered by the program's region
            return Promise.all([
                LimeController.getApprovedTemplates(),
                LimeController.getProductsByRegion(programData.regionId)
            ]).then(([templatesData, productsData]) => {
                setTemplates(templatesData)
                setProducts(productsData)

                // Calculate rows from template (FIX 3+15: pass product data and depths)
                if (programData.templateData) {
                    const ps = programData.productSources
                    const findProd = (pid: number | null) => pid ? productsData.find(p => p.id === pid) : undefined
                    const opts: CalcOptions = {
                        limeGypsumDepthCm: ps.limeGypsumDepthCm,
                        pkDepthCm: ps.pkDepthCm,
                        products: buildProductValues(
                            findProd(ps.calciticLimeProductId),
                            findProd(ps.dolomiticLimeProductId),
                            findProd(ps.gypsumProductId),
                            findProd(ps.potassiumProductId),
                            findProd(ps.phosphateProductId)
                        )
                    }
                    setCalculatedRows(calculateLandRows(programData.labInputs, programData.templateData, opts))
                }

                // Store original for change detection
                originalRef.current = {
                    name: programData.name,
                    productSources: programData.productSources,
                    templateId: programData.limeTemplateId,
                    labInputs: programData.labInputs
                }

                setLoading(false)
            })
        }).catch(() => {
            context.showSnack(<Failed title={context.word('failed_to_load')}/>)
            navigate('/liming/programs')
        })
    }, [id])

    // Recalculate when lab inputs, products, or depths change
    useEffect(() => {
        if (program?.templateData) {
            setCalculatedRows(calculateLandRows(labInputs, program.templateData, buildCalcOptions()))
        }
    }, [labInputs, program?.templateData, productSources, products])

    // Detect unsaved changes
    const hasChanges = (): boolean => {
        if (!originalRef.current || !program) return false

        if (name !== originalRef.current.name) return true
        if (selectedTemplateId !== originalRef.current.templateId) return true
        if (JSON.stringify(productSources) !== JSON.stringify(originalRef.current.productSources)) return true
        if (labInputsChanged) return true

        return false
    }

    // Block navigation when there are unsaved changes
    const blocker = useBlocker(() => hasChanges())

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (hasChanges()) {
                event.preventDefault()
            }
        }
        window.addEventListener('beforeunload', handleBeforeUnload)
        return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }, [name, productSources, selectedTemplateId, labInputs, labInputsChanged])

    function saveProgram(): Promise<void> {
        if (!program || !productSources || !selectedTemplateId || !regionId) {
            return Promise.reject()
        }

        // Merge calculated output values into lab input rows before saving
        // This stores the frontend-calculated values so Excel export can read them directly
        const labInputsWithCalcs: LimeProgramLabInputs = {
            rows: labInputs.rows.map((row, idx) => {
                const calc = calculatedRows[idx]
                if (!calc) return row
                return { ...row, outputs: extractCalculatedOutputs(calc) }
            })
        }

        return showSuccessOrFailed(
            context,
            LimeController.updateProgram({
                id: program.id,
                name,
                landId: program.landId,
                regionId,
                limeTemplateId: selectedTemplateId,
                productSources,
                labInputs: labInputsWithCalcs
            }),
            'successfully_saved'
        ).then(() => {
            // Update original ref after save
            originalRef.current = {
                name,
                productSources,
                templateId: selectedTemplateId,
                labInputs
            }
            setLabInputsChanged(false)

            // Reset approval status after saving (must resubmit)
            if (program) {
                setProgram({
                    ...program,
                    submittedOn: null,
                    approvedOn: null,
                    approvedBy: null
                })
            }
        })
    }

    function submitProgram(approverId: number) {
        showSuccessOrFailed(context, LimeController.submitProgram({id, approverId}))
            .then(() => {
                setShowApprover(false)
                if (program) {
                    setProgram({
                        ...program,
                        submittedOn: new Date(),
                        assignedApproverId: approverId
                    })
                }
            })
    }

    function finishProgram() {
        const doFinish = () => {
            showSuccessOrFailed(context, LimeController.finishProgram(id), context.word('auto_approved'))
                .then(() => {
                    if (program) {
                        const now = new Date()
                        setProgram({
                            ...program,
                            submittedOn: now,
                            approvedOn: now,
                            autoApproved: true
                        })
                    }
                })
        }

        if (somethingChanged) {
            saveProgram().then(doFinish)
        } else {
            doFinish()
        }
    }

    // Build CalcOptions from current product selections and depths (FIX 3 + FIX 15)
    function buildCalcOptions(): CalcOptions {
        const findProduct = (id: number | null | undefined) =>
            id ? products.find(p => p.id === id) : undefined

        return {
            limeGypsumDepthCm: productSources?.limeGypsumDepthCm,
            pkDepthCm: productSources?.pkDepthCm,
            products: buildProductValues(
                findProduct(productSources?.calciticLimeProductId),
                findProduct(productSources?.dolomiticLimeProductId),
                findProduct(productSources?.gypsumProductId),
                findProduct(productSources?.potassiumProductId),
                findProduct(productSources?.phosphateProductId)
            )
        }
    }

    function updateLabInputs(newLabInputs: LimeProgramLabInputs) {
        setLabInputs(newLabInputs)
        setLabInputsChanged(true)
    }

    // Update a single override value for a row
    function updateRowOverride(index: number, field: 'limeTypeOverride' | 'limeNeedOverride' | 'gypsumNeedOverride' | 'kNeedOverride' | 'pNeedOverride' | 'elementalSulphurOverride', value: string | number | null) {
        const newRows = [...labInputs.rows]
        const row = newRows[index]
        if (!row) return
        newRows[index] = {...row, [field]: value}
        updateLabInputs({rows: newRows})
    }

    // Update override value for multiple rows at once
    function updateMultipleRowOverrides(indices: number[], field: 'limeTypeOverride' | 'limeNeedOverride' | 'gypsumNeedOverride' | 'kNeedOverride' | 'pNeedOverride' | 'elementalSulphurOverride', value: string | number | null) {
        const newRows = [...labInputs.rows]
        indices.forEach(index => {
            const row = newRows[index]
            if (row) {
                newRows[index] = {...row, [field]: value}
            }
        })
        updateLabInputs({rows: newRows})
    }

    if (loading) {
        return <div className="p-4">{context.word('loading')}...</div>
    }

    if (!program) {
        return <div className="p-4">{context.word('program_not_found')}</div>
    }

    const isSubmitted = program.submittedOn !== null
    const isApproved = program.approvedOn !== null
    const somethingChanged = hasChanges()
    const hasOverrides = labInputs.rows.some(r =>
        r.limeTypeOverride !== null || r.limeNeedOverride !== null ||
        r.gypsumNeedOverride !== null || r.kNeedOverride !== null ||
        r.pNeedOverride !== null || r.elementalSulphurOverride !== null
    )
    const canAutoApprove = program.templateApproved && !hasOverrides

    return (
        <div className="flex flex-col h-screen overflow-hidden">
            {/* Dialogs */}
            <Dialog
                title={context.word('select_approver')}
                body={<ProgramApprove onCancel={() => setShowApprover(false)} onSubmit={submitProgram} fetchApprovers={UserController.limingApprovers}/>}
                show={showApprover}
                setShow={setShowApprover}
            />

            <Dialog
                title={context.word('warning')}
                show={warningPopup}
                setShow={setWarningPopup}
                body={
                    <div className="p-2 text-xl">
                        <div>{context.word('must_resubmit_program_for_approval')}. {context.word('would_you_like_to_continue')}?</div>
                        <div className="flex m-2 text-white justify-center">
                            <button
                                className="bg-primary p-2 rounded m-1 px-4"
                                onClick={() => {
                                    saveProgram()
                                    setWarningPopup(false)
                                }}
                            >
                                {context.word('yes')}
                            </button>
                            <button
                                className="bg-error-500 p-2 rounded m-1 px-4"
                                onClick={() => setWarningPopup(false)}
                            >
                                {context.word('no')}
                            </button>
                        </div>
                    </div>
                }
            />

            <Dialog
                title={context.word('save_changes')}
                show={blocker.state === 'blocked'}
                setShow={() => blocker.reset?.()}
                body={
                    <div className="p-4">
                        <div className="text-center mb-4">
                            <div>{context.word('unsaved_changes_lost')}.</div>
                            <div>{context.word('save_before_closing')}?</div>
                        </div>
                        <div className="flex w-full justify-between items-center">
                            <div
                                className="uppercase text-error-400 pr-4 cursor-pointer underline"
                                onClick={() => blocker.proceed?.()}
                            >
                                {context.word('discard_changes')}
                            </div>
                            <div
                                className="btn bg-primary"
                                onClick={() => {
                                    saveProgram().then(() => {
                                        blocker.proceed?.()
                                    })
                                }}
                            >
                                {context.word('save')}
                            </div>
                        </div>
                    </div>
                }
            />

            {/* Tab Navigation - same styling as fertiliser program */}
            <div className="sticky top-0 z-10">
                <div className="bg-gray-100 flex overflow-x-scroll pt-1 text-sm items-center overflow-hidden">
                    <div className="px-4"></div>
                    <div className="cursor-pointer">
                        <ChevronLeftIcon className="text-gray-500 h-4" onClick={() => setTab(tab === 'playfield' ? 'master' : 'playfield')}/>
                    </div>
                    <div className="cursor-pointer">
                        <ChevronRightIcon className="text-gray-500 h-4" onClick={() => setTab(tab === 'master' ? 'playfield' : 'master')}/>
                    </div>
                    <div className="w-4"></div>

                    {/* Master Tab */}
                    <div
                        className={tab === 'master'
                            ? 'flex bg-white px-2 py-1 border-l border-r border-r-4 border-gray-300 rounded-t border-l-gray-300 border-t-2 border-t-primary-600 cursor-pointer'
                            : 'flex px-2 py-1 border-r-4 border-gray-100 cursor-pointer'}
                        onClick={() => setTab('master')}
                    >
                        <span className="text-red-500">{somethingChanged ? '*' : ' '}</span>
                        <span className="px-2 align-middle text-black whitespace-nowrap">{context.word('master')}</span>
                    </div>

                    {/* Playfield Tab */}
                    <div
                        className={tab === 'playfield'
                            ? 'flex bg-white px-2 py-1 border-l border-r border-r-4 border-gray-300 rounded-t border-l-gray-300 border-t-2 border-t-primary-600 cursor-pointer'
                            : 'flex px-2 py-1 border-r-4 border-gray-100 cursor-pointer'}
                        onClick={() => setTab('playfield')}
                    >
                        <span className="text-red-500">{labInputsChanged ? '*' : ' '}</span>
                        <span className="px-2 align-middle text-black whitespace-nowrap">{program.landName || context.word('playfield')}</span>
                    </div>
                </div>
            </div>

            {/* Tab Content */}
            {switcher(tab, c => c
                .case('master', () => (
                    <div className="flex-1 overflow-auto">
                        <div className="flex w-full justify-center">
                            <div className="pt-4">
                                <div className="text-sm text-gray-600">{dateFormat(new Date())}</div>
                                <div className="font-bold uppercase text-primary-800 tracking-wider text-xl leading-none">
                                    {program.clientName}
                                </div>

                                <table>
                                    <tbody>
                                        <tr>
                                            <td className="p-1">{context.word('program_name')}:</td>
                                            <td className="p-1">
                                                <Input
                                                    value={name}
                                                    change={setName}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('farm')}:</td>
                                            <td className="p-1">
                                                <span className="px-2">{program.farmName}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.wordUnit('land', 'ranch')}:</td>
                                            <td className="p-1">
                                                <span className="px-2">{program.landName}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('region')}:</td>
                                            <td className="p-1">
                                                <span className="px-2">{regionName}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('template')}:</td>
                                            <td className="p-1">
                                                <SelectNumber
                                                    options={templates}
                                                    textFunc={t => t.name}
                                                    valueFunc={t => t.id}
                                                    value={selectedTemplateId || 0}
                                                    onChange={v => setSelectedTemplateId(v)}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('calcitic_lime_product')}:</td>
                                            <td className="p-1">
                                                <SelectNumberNullable
                                                    options={products.filter(p => p.type === LimeProductType.Calcite && p.active)}
                                                    textFunc={p => p.name}
                                                    valueFunc={p => p.id}
                                                    value={productSources?.calciticLimeProductId ?? null}
                                                    onChange={v => productSources && setProductSources({...productSources, calciticLimeProductId: v})}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('dolomitic_lime_product')}:</td>
                                            <td className="p-1">
                                                <SelectNumberNullable
                                                    options={products.filter(p => p.type === LimeProductType.Dolomite && p.active)}
                                                    textFunc={p => p.name}
                                                    valueFunc={p => p.id}
                                                    value={productSources?.dolomiticLimeProductId ?? null}
                                                    onChange={v => productSources && setProductSources({...productSources, dolomiticLimeProductId: v})}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('gypsum_product')}:</td>
                                            <td className="p-1">
                                                <SelectNumberNullable
                                                    options={products.filter(p => p.type === LimeProductType.Gypsum && p.active)}
                                                    textFunc={p => p.name}
                                                    valueFunc={p => p.id}
                                                    value={productSources?.gypsumProductId ?? null}
                                                    onChange={v => productSources && setProductSources({...productSources, gypsumProductId: v})}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('potassium_source')}:</td>
                                            <td className="p-1">
                                                <SelectNumberNullable
                                                    options={products.filter(p => p.type === LimeProductType.Potassium && p.active)}
                                                    textFunc={p => p.name}
                                                    valueFunc={p => p.id}
                                                    value={productSources?.potassiumProductId ?? null}
                                                    onChange={v => productSources && setProductSources({...productSources, potassiumProductId: v})}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('phosphate_source')}:</td>
                                            <td className="p-1">
                                                <SelectNumberNullable
                                                    options={products.filter(p => p.type === LimeProductType.Phosphate && p.active)}
                                                    textFunc={p => p.name}
                                                    valueFunc={p => p.id}
                                                    value={productSources?.phosphateProductId ?? null}
                                                    onChange={v => productSources && setProductSources({...productSources, phosphateProductId: v})}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('lime_gypsum_depth')} (cm):</td>
                                            <td className="p-1">
                                                <Number
                                                    value={productSources?.limeGypsumDepthCm ?? 30}
                                                    change={v => productSources && setProductSources({...productSources, limeGypsumDepthCm: v})}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1">{context.word('pk_depth')} (cm):</td>
                                            <td className="p-1">
                                                <Number
                                                    value={productSources?.pkDepthCm ?? 30}
                                                    change={v => productSources && setProductSources({...productSources, pkDepthCm: v})}
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div className="flex text-sm">
                                    <div className="flex-1">
                                        <div className="font-bold text-primary-800 tracking-wider pl-2">{context.word('status')}</div>
                                        <div className="pl-2">
                                            <img
                                                className="text-red-800 stoke-current px-1 w-6 h-6 inline-block align-middle"
                                                src="/icons/irrigation.svg" alt="created"/>
                                            <span className="align-middle">{context.word('created')}: {dateFormat(program.created)}</span>
                                        </div>
                                        <div className="pl-2">
                                            <img
                                                className="text-red-800 stoke-current px-1 w-6 h-6 inline-block align-middle"
                                                src="/icons/aim.svg" alt="updated"/>
                                            <span className="align-middle">{context.word('updated')}: {dateFormat(program.updated)}</span>
                                        </div>
                                        {isSubmitted && (
                                            <div className="pl-2">
                                                <img
                                                    className="text-red-800 stoke-current px-1 w-6 h-6 inline-block align-middle"
                                                    src="/icons/trees.svg" alt="submitted"/>
                                                <span className="align-middle">{context.word('submitted')}: {dateFormat(program.submittedOn)}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex-1 text-sm">
                                        <div className="font-bold text-primary-800 tracking-wider pl-2">{context.word('approval')}</div>
                                        {isApproved ? (
                                            <>
                                                <div className="pl-2">
                                                    <img
                                                        className="text-red-800 stoke-current px-1 w-6 h-6 inline-block align-middle"
                                                        src="/icons/irrigation.svg" alt="approved"/>
                                                    <span className="align-middle">{program.approvedBy}{program.autoApproved ? ` (${context.word('auto_approved')})` : ''}</span>
                                                </div>
                                                <div className="pl-2">
                                                    <img
                                                        className="text-red-800 stoke-current px-1 w-6 h-6 inline-block align-middle"
                                                        src="/icons/aim.svg" alt="approved date"/>
                                                    <span className="align-middle">{dateFormat(program.approvedOn)}</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="pl-2">{context.word('not_yet_approved')}</div>
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                ))
                .case('playfield', () => {
                    
                    const productNames = getProductNames(products, productSources, context);
                    
                    return (
                        <div className="p-4 flex-1 min-h-0 flex flex-col overflow-hidden">
                            <LimePlayfieldUnified
                                data={labInputs}
                                setData={updateLabInputs}
                                calculatedRows={calculatedRows}
                                updateRowOverride={updateRowOverride}
                                updateMultipleRowOverrides={updateMultipleRowOverrides}
                                labInputUnit={labInputUnit}
                                landId={program.landId}
                                products={productNames}
                            />
                        </div>
                    )
                })
                .default(() => null)
            )}

            {/* Application Data Dialog */}
            <ApplicationDataDialog
                show={showApplicationData}
                setShow={setShowApplicationData}
                labInputs={labInputs}
                calculatedRows={calculatedRows}
                boundaryCoordinates={boundaryCoordinates}
                boundaryLoading={boundaryLoading}
                boundaryError={boundaryError}
                landId={program?.landId}
                programId={id}
                labInputUnit={labInputUnit}
            />

            {/* Footer Actions */}
            <div className="text-right pr-8">
                {calculatedRows.length > 0 && (
                    <div
                        className={`btn bg-secondary m-2 ${somethingChanged ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => !somethingChanged && setShowApplicationData(true)}
                        title={somethingChanged ? context.word('save_before_interpolation') : ''}
                    >
                        {context.word('qc_map')}
                    </div>
                )}

                {somethingChanged && (
                    <div
                        className="btn bg-primary m-2"
                        onClick={() => {
                            if (isSubmitted || isApproved) {
                                setWarningPopup(true)
                            } else {
                                saveProgram()
                            }
                        }}
                    >
                        {context.word('save')}
                    </div>
                )}

                {!props.footer && !isSubmitted && !isApproved && canAutoApprove && (
                    <div
                        className="btn bg-primary m-2"
                        onClick={finishProgram}
                    >
                        {context.word('finish')}
                    </div>
                )}

                {!props.footer && !isSubmitted && !isApproved && !canAutoApprove && (
                    <div
                        className="btn bg-primary m-2"
                        onClick={() => setShowApprover(true)}
                    >
                        {context.word('submit_approval')}
                    </div>
                )}

                {!props.footer && isSubmitted && !isApproved && (
                    <div className="btn bg-gray-500 m-2">
                        {context.word('waiting_approval')}
                    </div>
                )}

                {props.footer}
            </div>
        </div>
    )
}

export default LimeProgram
